import styles from "./my-order-item.module.css";
import { useEffect, useState } from "react";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { Order } from "entity-store/orders-store/use-orders-store";
import { PosterProject } from "PosterEditor/model/poster-project";
import { useCheckoutPricing } from "hooks/use-checkout-pricing";
import { ProjectPreview } from "components/common/project-preview";
import { formatPrice } from "PosterEditor/utils/format-price";
import { CTAButton } from "BEDROCK/shared-components/cta-button";
import { useDuplicateProject } from "admin/hooks/use-duplicate-project";
import { DraftAlertModal } from "BEDROCK/shared-components/draft-alert-modal";
import { useSizePresets, SizePreset } from "PosterEditor/model/size-presets/size-preset";

interface MyOrderItemProps {
  order: Order;
}

export const MyOrderItem = (props: MyOrderItemProps) => {
  const { loadProjects } = useProjectStore();
  const [projects, setProjects] = useState<PosterProject[]>([]);

  useEffect(() => {
    const ids = props.order.projectIDs;
    if (ids.length > 0) {
      const projects = loadProjects(ids);
      setProjects(projects);
    }
  }, []);

  const price = formatPrice(props.order.totalPrice);

  return (
    <div className={styles.container}>
      <div className={styles.orderInfoSection}>
        <div className={styles.orderDate}>
          <h2 className={styles.sectionTitle}>Order Date:</h2>
          <p>
            {new Date(props.order.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
        {props.order.shippingAddress && (
          <div className={styles.shippingInfo}>
            <h2 className={styles.sectionTitle}>Shipping to:</h2>
            <div className={styles.addressBlock}>
              <p>{props.order.shippingAddress.line1}</p>
              {props.order.shippingAddress.line2 && <p>{props.order.shippingAddress.line2}</p>}
              <p>{props.order.shippingAddress.city}</p>
              <p>{props.order.shippingAddress.postcode}</p>
              <p>{props.order.shippingAddress.country}</p>
            </div>
            <p className={styles.deliveryInfo}>Standard delivery 4-6 business days</p>
          </div>
        )}
        <div className={styles.totalSection}>
          <h2 className={styles.sectionTitle}>Total:</h2>
          <p className={styles.totalPrice}>{price}</p>
        </div>
      </div>
      <div className={styles.projectListContainer}>
        {projects.map((project) => (
          <ProjectItem project={project} key={project.id} />
        ))}
      </div>
    </div>
  );
};

interface ProjectItemProps {
  project: PosterProject;
}

const ProjectItem = (props: ProjectItemProps) => {
  const { formattedPriceForProject } = useCheckoutPricing();
  const [canShowDraftAlert, setCanShowDraftAlert] = useState(false);
  const { duplicateProject } = useDuplicateProject();
  const { removeAllNotInCheckout } = useProjectStore();
  const { sizePresetFromProject } = useSizePresets();
  const [sizePreset, setSizePreset] = useState<SizePreset | undefined>();

  useEffect(() => {
    const size = sizePresetFromProject(props.project);
    setSizePreset(size);
  }, []);

  const price = formattedPriceForProject(props.project);
  const size = sizePreset?.sizeDescription ?? "-";
  const material = sizePreset?.materialDescription ?? "";

  const copyProject = async () => {
    try {
      await removeAllNotInCheckout(props.project.id);
      const newProject = await duplicateProject.mutateAsync(props.project);
      if (newProject) {
        window.location.href = `/project/${newProject.id}`;
      }
    } catch (error) {
      console.error("Failed to duplicate project:", error);
    }
  };

  return (
    <div className={styles.projectItem}>
      <div className={styles.projectPreview}>
        <ProjectPreview
          project={props.project}
          size={{ width: 200, height: 200 }}
          showOutline
        />
      </div>
      <div className={styles.projectItemInfo}>
        <div className={styles.leadingContent}>
          <h3>Physical Print</h3>
          <p>{size}</p>
          <span>{material}</span>
        </div>
        <div className={styles.trailingContent}>
          <h3 className={styles.price}>{price}</h3>
          <CTAButton
            variant="LINK"
            title="COPY"
            onClick={() => {
              setCanShowDraftAlert(true);
            }}
          />
        </div>
      </div>
      <DraftAlertModal
        title="Start a new project?"
        message="You have a draft project that hasn't been saved. If you copy this project, your current draft will be lost."
        canShow={canShowDraftAlert}
        onCancel={() => {
          setCanShowDraftAlert(false);
        }}
        onOverride={() => {
          copyProject();
        }}
        cancelCTA="Cancel"
        overrideCTA="Continue"
      />
    </div>
  );
};
